"use client";

import { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useOrganizationStore } from "@/stores/organization";
import { useUserStore } from "@/stores/user";
import {
  getPaymentMethodByPriceId,
  getProductNameByProduct,
  getSubscriptionMetadata,
  getSubscriptionProductIdByProduct,
  PaymentMethod,
  PaymentMethods,
  Product,
  Subscription,
  SubscriptionCollectionMethod,
  SubscriptionCustomer,
} from "@/types/subscription";
import { MsgQuestion } from "@/components/msg-question";

import PowerOnPro from "@/components/icons/poweron-pro";
import PowerOnPipelines from "@/components/icons/poweron-pipelines";
import SubscriptionPowerOnPro from "@/components/subscription-poweron-pro";
import SubscriptionPowerOnPipelines from "@/components/subscription-poweron-pipelines";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  DEFAULT_DEBOUNCE_DELAY,
  DEFAULT_ERROR_TITLE,
  DEFAULT_MISSING_PERMISSION_TITLE,
} from "@/lib/constants";
import {
  createCustomer,
  getCustomerFromSessionID,
  searchCustomerByOwnerEmail,
} from "@/services/customer.client";
import { formatLongDate, overrideMessageIf403 } from "@/utils/utils";
import { useDebounce } from "@/hooks/useDebounce";
import {
  createSubscription,
  getCheckoutUrl,
  getSubscriptionUrl,
  validatePaymentLinkAccess,
} from "@/services/subscription.client";
import { Button } from "@/components/ui/button";
import { Clock, LoaderCircle, Sparkles } from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import SubscriptionPaymentMethodDialog from "@/components/subscription-payment-method-dialog";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function SettingsSubscriptions() {
  useDocumentTitle("Subscriptions");
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const productParam = searchParams.get("type") as Product | null;
  const sessionId = searchParams.get("session_id");

  const { user } = useUserStore();
  const {
    organization,
    members,
    subscriptions,
    apiKeys,
    taxExemption,
    updateOrganization,
    setError: setOrganizationError,
  } = useOrganizationStore();

  const [isAdmin, setIsAdmin] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMatchLoading, setIsMatchLoading] = useState(false);
  const [isManageLoading, setIsManageLoading] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("yearly");
  const [product, setProduct] = useState<Product>("poweron-pro");

  const calculatePercentage = (): string => {
    const paymentMethod = getPaymentMethodByPriceId(subscription!.priceId)!;
    const metadata = getSubscriptionMetadata(paymentMethod);

    switch (metadata?.term) {
      case "month":
        return "5%";
      case "year":
        return "10%";
      default:
        return "";
    }
  };

  const calculateSavings = (): string => {
    const currentPaymentMethod =
      getPaymentMethodByPriceId(subscription!.priceId) || paymentMethod;
    const metadata = getSubscriptionMetadata(currentPaymentMethod);
    const totalSeats = subscription?.seats || 1;

    let savings = 0;

    switch (true) {
      case metadata?.term === "month":
        savings =
          PaymentMethods.MONTHLY.powerOnProPrice * 12 * totalSeats -
          PaymentMethods.YEARLY.powerOnProPrice * totalSeats;
        break;
      case metadata?.term === "year" && product === "poweron-pro":
        savings =
          PaymentMethods.YEARLY.powerOnProPrice * 3 * totalSeats -
          PaymentMethods.THREE_YEAR.powerOnProPrice * totalSeats;
        break;
      case metadata?.term === "year" && product === "poweron-pipelines":
        savings =
          PaymentMethods.YEARLY.powerOnPipelinesPrice * 3 * totalSeats -
          PaymentMethods.THREE_YEAR.powerOnPipelinesPrice * totalSeats;
        break;
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    })
      .format(savings)
      .slice(0, -3);
  };

  const handleCancelSubscription = async () => {
    if (organization === null) return;

    try {
      setIsCancelLoading(true);

      const url = await getSubscriptionUrl(
        organization.id,
        organization.customerId,
      );

      if (url !== "" && subscription && subscription.canceledAt === null) {
        window.open(`${url}/subscriptions/${subscription.id}/cancel`, "_self");
      } else {
        toast.error(DEFAULT_ERROR_TITLE, {
          description:
            "We're unable to create a Stripe billing session. Please try again later.",
        });
      }
    } catch (error: any) {
      toast.error(
        ...overrideMessageIf403(error.message, [
          DEFAULT_ERROR_TITLE,
          {
            description:
              "There was a problem creating the Stripe billing session. Please try again later.",
          },
        ]),
      );
      console.warn(error.message);
      setOrganizationError(error.message);
    }
  };

  const handleManageSubscription = async (isUpsell = false) => {
    if (organization === null) return;

    // @TODO: For invoiced subscriptions, we need to support seat/hosts changes
    // within Portal and update the subscription in Stripe

    try {
      if (!isUpsell) {
        setIsManageLoading(true);
      }

      const url = await getSubscriptionUrl(
        organization.id,
        organization.customerId,
      );

      if (url !== "") {
        window.open(url, "_self");
      } else {
        toast.error(DEFAULT_ERROR_TITLE, {
          description:
            "We're unable to create a Stripe billing session. Please try again later.",
        });
      }
    } catch (error: any) {
      toast.error(
        ...overrideMessageIf403(error.message, [
          DEFAULT_ERROR_TITLE,
          {
            description:
              "There was a problem creating the Stripe billing session. Please try again later.",
          },
        ]),
      );
      console.warn(error.message);
      setOrganizationError(error.message);
    }
  };

  const handleSubscribe = async (
    collectionMethod?: SubscriptionCollectionMethod,
    customer?: SubscriptionCustomer | string,
    quantity?: number,
    promotionCodeId?: string,
  ) => {
    if (organization === null || user === null) return;

    // Protect against non-admins and non-owners
    if (user.id !== organization.ownerId && !isAdmin) {
      toast.error(DEFAULT_MISSING_PERMISSION_TITLE, {
        description:
          "You must be the organization owner or an admin to subscribe",
      });
      return;
    }

    // Monthly subscription (only PowerOn Pro+)
    if (paymentMethod === PaymentMethods.MONTHLY.type) {
      // Use checkout session for tax exempt customers or existing customers (to reuse payment methods)
      if (
        (taxExemption && taxExemption.status === "approved") ||
        organization.customerId
      ) {
        await handleTaxExemptSubscription();
        return;
      }

      try {
        await validatePaymentLinkAccess(organization.id);

        const baseUrl =
          process.env.NEXT_PUBLIC_STRIPE_POWERON_MONTHLY_PAYMENT_URL!;
        const url = `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}prefilled_email=${user.email}`;

        window.open(url, "_self");
      } catch (error: any) {
        toast.error(
          ...overrideMessageIf403(error.message, [
            DEFAULT_ERROR_TITLE,
            {
              description:
                "There was a problem navigating to the Stripe payment link. Please try again later.",
            },
          ]),
        );
        setOrganizationError(error.message);
        console.warn(error.message);
      }
      return;
    }

    if (collectionMethod === SubscriptionCollectionMethod.AUTOMATIC) {
      // Use checkout session for tax exempt customers or existing customers (to reuse payment methods)
      if (
        (taxExemption && taxExemption.status === "approved") ||
        organization.customerId
      ) {
        await handleTaxExemptSubscription();
        return;
      }

      // Handle new customers via payment links
      try {
        await validatePaymentLinkAccess(organization.id);

        const metadata = getSubscriptionMetadata(paymentMethod);
        const baseUrl =
          product === "poweron-pro"
            ? metadata.powerOnProPaymentUrl!
            : metadata.powerOnPipelinesPaymentUrl!;
        const url = `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}prefilled_email=${user.email}`;

        window.open(url, "_self");
      } catch (error: any) {
        toast.error(
          ...overrideMessageIf403(error.message, [
            DEFAULT_ERROR_TITLE,
            {
              description:
                "There was a problem navigating to the Stripe payment link. Please try again later.",
            },
          ]),
        );
        setOrganizationError(error.message);
        console.warn(error.message);
      }

      return;
    }

    if (collectionMethod === SubscriptionCollectionMethod.INVOICE) {
      try {
        const customerId =
          typeof customer === "string"
            ? customer
            : await createCustomer(organization.id, customer!);

        if (!customerId) {
          toast.error(DEFAULT_ERROR_TITLE, {
            description:
              "There was a problem creating the Stripe customer. Please try again later.",
          });
          setIsDialogOpen(false);
          return;
        }

        const metadata = getSubscriptionMetadata(paymentMethod);
        const priceId =
          product === "poweron-pro"
            ? metadata.powerOnProPriceId
            : metadata.powerOnPipelinesPriceId;

        const subscription = await createSubscription(
          organization.id,
          customerId,
          product,
          priceId!,
          quantity!,
          promotionCodeId,
        );

        if (!subscription) {
          toast.error(DEFAULT_ERROR_TITLE, {
            description:
              "There was a problem creating the Stripe subscription. Please try again later.",
          });
          setIsDialogOpen(false);
          return;
        }

        await updateOrganization({
          id: organization.id,
          customerId: customerId,
        });

        setIsDialogOpen(false);
        toast.success("Subscription Created", {
          description: `Your subscription for ${getProductNameByProduct(product)} has been created. An invoice will be sent to your billing email shortly`,
        });
        return;
      } catch (error: any) {
        toast.error(
          ...overrideMessageIf403(error.message, [
            DEFAULT_ERROR_TITLE,
            {
              description:
                "There was a problem creating the Stripe customer & subscription. Please try again later.",
            },
          ]),
        );
        setIsDialogOpen(false);
        setOrganizationError(error.message);
        console.warn(error.message);
      }
    }

    // Prompt for selecting a collection method
    setIsDialogOpen(true);
  };

  const handleSubscriptionMatch = async () => {
    if (organization === null || user === null) return;

    // Protect against non-admins and non-owners
    if (user.id !== organization.ownerId && !isAdmin) {
      toast.error(DEFAULT_MISSING_PERMISSION_TITLE, {
        description:
          "You must be an organization admin to match a subscription",
      });
      return;
    }

    try {
      setIsMatchLoading(true);

      const customers = await searchCustomerByOwnerEmail(
        organization.id,
        organization.ownerId,
      );

      if (customers.length === 1) {
        const customer = customers[0];

        await updateOrganization({
          id: organization.id,
          customerId: customer.id,
        });

        toast.success("Subscription Updated", {
          description:
            "Your subscription has been matched with your organization successfully",
        });
      } else if (customers.length > 1) {
        toast.warning("Multiple Subscriptions Found", {
          description:
            "We found multiple subscriptions. Please contact support.",
        });
      } else {
        toast.error("No Subscriptions Found", {
          description: `We weren't able to find any subscriptions for ${user?.email}.`,
        });
      }
    } catch (error: any) {
      toast.error(
        ...overrideMessageIf403(error.message, [
          DEFAULT_ERROR_TITLE,
          {
            description:
              "There was a problem matching your subscription. If this continues, please contact support",
          },
        ]),
      );
      console.warn(error.message);
      setOrganizationError(error.message);
    } finally {
      setIsMatchLoading(false);
    }
  };

  const handleTaxExemptSubscription = async () => {
    try {
      const metadata = getSubscriptionMetadata(paymentMethod);
      const priceId =
        product === "poweron-pro"
          ? metadata.powerOnProPriceId
          : metadata.powerOnPipelinesPriceId;

      const url = await getCheckoutUrl(
        organization!.id,
        organization!.customerId,
        product,
        priceId!,
      );
      if (url) {
        window.open(url, "_self");
      } else {
        toast.error(DEFAULT_ERROR_TITLE, {
          description:
            "There was a problem creating the Stripe checkout session. Please try again later.",
        });
      }
      return;
    } catch (error: any) {
      toast.error(
        ...overrideMessageIf403(error.message, [
          DEFAULT_ERROR_TITLE,
          {
            description:
              "There was a problem creating the Stripe checkout session. Please try again later.",
          },
        ]),
      );
      setOrganizationError(error.message);
      console.warn(error.message);
    }
  };

  const handleSubscriptionQuantity = async () => {
    if (organization === null) return;

    try {
      const url = await getSubscriptionUrl(
        organization.id,
        organization.customerId,
      );

      if (url !== "" && subscription) {
        window.open(`${url}/subscriptions/${subscription.id}/update`, "_self");
      } else {
        toast.error(DEFAULT_ERROR_TITLE, {
          description:
            "We're unable to create a Stripe billing session. Please try again later.",
        });
      }
    } catch (error: any) {
      toast.error(
        ...overrideMessageIf403(error.message, [
          DEFAULT_ERROR_TITLE,
          {
            description:
              "There was a problem creating the Stripe billing session. Please try again later.",
          },
        ]),
      );
      console.warn(error.message);
      setOrganizationError(error.message);
    }
  };

  const handleRenewSubscription = async () => {
    if (organization === null) return;

    try {
      const url = await getSubscriptionUrl(
        organization.id,
        organization.customerId,
      );

      if (url !== "" && subscription && subscription.canceledAt !== null) {
        window.open(
          `${url}/subscriptions/${subscription.id}/reactivate`,
          "_self",
        );
      } else {
        toast.error(DEFAULT_ERROR_TITLE, {
          description:
            "We're unable to create a Stripe billing session. Please try again later.",
        });
      }
    } catch (error: any) {
      toast.error(
        ...overrideMessageIf403(error.message, [
          DEFAULT_ERROR_TITLE,
          {
            description:
              "There was a problem creating the Stripe billing session. Please try again later.",
          },
        ]),
      );
      console.warn(error.message);
      setOrganizationError(error.message);
    }
  };

  const debounceSubscriptionMatch = useDebounce(
    handleSubscriptionMatch,
    DEFAULT_DEBOUNCE_DELAY,
  );

  useEffect(() => {
    const updateOrganizationWithCustomerId = async (sessionId: string) => {
      try {
        const customerId = await getCustomerFromSessionID(sessionId);

        if (customerId && organization) {
          await updateOrganization({
            id: organization.id,
            customerId,
          });
        }
      } catch (error: any) {
        console.warn(error.message);
      } finally {
        router.refresh();
      }
    };

    // Update organization with customer ID
    if (sessionId) {
      updateOrganizationWithCustomerId(sessionId);
    }
  }, [router, sessionId, pathname, organization, updateOrganization]);

  useEffect(() => {
    if (
      productParam &&
      (productParam === "poweron-pro" || productParam === "poweron-pipelines")
    ) {
      setProduct(productParam);
    } else {
      setProduct("poweron-pro");
    }
  }, [productParam]);

  useEffect(() => {
    setIsAdmin(members.find((m) => m.userId === user?.id)?.role === "admin");
  }, [members, user]);

  useEffect(() => {
    setSubscription(
      subscriptions.find(
        (s) => s.productId === getSubscriptionProductIdByProduct(product),
      ) || null,
    );
    setPaymentMethod(
      subscription === null
        ? "yearly"
        : getPaymentMethodByPriceId(subscription.priceId) || "yearly",
    );
  }, [subscriptions, subscription, product]);

  return (
    <>
      <div>
        <h2 className="text-xl font-semibold leading-6 text-gray-1100 dark:text-gray-100">
          Subscriptions<span className="text-muted">{" > "}</span>
          {getProductNameByProduct(product)}
        </h2>
        <p className="mt-1">
          Manage your subscriptions, billing details, and invoices
        </p>
      </div>
      <div className="flex flex-col">
        <div className="mt-4 flex w-full flex-col items-start gap-3 lg:flex-row lg:items-center">
          <Tabs
            value={product}
            className="inline-flex w-full lg:mr-3 lg:w-[333px]"
            onValueChange={(value) => {
              if (productParam) {
                router.replace(`/settings/subscriptions`);
              }
              setProduct(value as Product);
            }}
          >
            <TabsList>
              <TabsTrigger value="poweron-pro" className="text-xs sm:text-sm">
                <PowerOnPro className="mr-1 mt-[1px] size-4 shrink-0 fill-foreground" />
                <span className="truncate">PowerOn Pro</span>
              </TabsTrigger>
              <TabsTrigger
                value="poweron-pipelines"
                className="text-xs sm:text-sm"
              >
                <PowerOnPipelines className="mr-1 mt-[1px] size-4 shrink-0 fill-foreground" />
                <span className="truncate">Pipelines</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {isAdmin && subscription && (
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center lg:ml-0">
              <Button
                onClick={() => handleManageSubscription()}
                className="w-full text-xs sm:w-auto sm:text-sm"
              >
                {isManageLoading && (
                  <LoaderCircle className="mr-1 size-4 animate-spin" />
                )}
                <span className="sm:hidden">Billing & Invoices</span>
                <span className="hidden sm:inline">
                  Update Billing & View Invoices
                </span>
              </Button>
              {subscription.canceledAt === null && (
                <Button
                  variant="destructive"
                  onClick={handleCancelSubscription}
                  className="w-full text-xs sm:w-auto sm:text-sm lg:ml-auto"
                >
                  {isCancelLoading && (
                    <LoaderCircle className="mr-1 size-4 animate-spin" />
                  )}
                  Cancel
                </Button>
              )}
            </div>
          )}
        </div>
        <div className="mt-4 flex w-full flex-col items-start lg:flex-row lg:items-center">
          <Tabs
            value={paymentMethod}
            className="inline-flex w-full lg:mr-3 lg:w-[400px]"
            onValueChange={(value) => setPaymentMethod(value as PaymentMethod)}
          >
            <TabsList>
              <TabsTrigger
                disabled={product === "poweron-pipelines"}
                value="monthly"
                className="px-2 text-xs sm:px-3 sm:text-sm"
              >
                Monthly
              </TabsTrigger>
              <TabsTrigger
                value="yearly"
                className="px-2 text-xs sm:px-3 sm:text-sm"
              >
                Yearly
                {product !== "poweron-pipelines" && (
                  <Badge
                    className="ml-1 bg-emerald-500 text-emerald-50 sm:ml-4"
                    variant="largeTag"
                  >
                    <span className="hidden sm:inline">Save </span>5%
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="three_year"
                className="px-2 text-xs sm:px-3 sm:text-sm"
              >
                3-Year
                <Badge
                  className="ml-1 bg-pink-400 text-pink-50 sm:ml-4"
                  variant="largeTag"
                >
                  <span className="hidden sm:inline">Save </span>10%
                </Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>
      {/* Renew subscription message */}
      {subscription && subscription.canceledAt !== null && (
        <div className="mt-8">
          <Card>
            <CardHeader className="p-4 sm:p-8">
              <CardTitle className="text-base sm:text-lg">
                Renew your subscription
              </CardTitle>
              <CardDescription className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-1 text-sm">
                  <span>Your</span>
                  {product === "poweron-pipelines" ? (
                    <span>PowerOn Pipelines</span>
                  ) : (
                    <span>
                      PowerOn Pro
                      <sup className="font-bold text-brand-accent">+</sup>
                    </span>
                  )}
                  <span>subscription expires on</span>
                  <span className="font-bold text-brand-accent">
                    {formatLongDate((subscription?.cancelAt || 0) + 86400)}
                  </span>
                  <Clock className="size-4" />
                </div>
                <Button
                  variant="secondary"
                  onClick={handleRenewSubscription}
                  className="w-full text-sm sm:w-auto"
                >
                  <Sparkles className="size-4" />
                  Renew
                </Button>
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      )}
      {/* Upsell subscription message */}
      {isAdmin &&
        subscription &&
        subscription.cancelAt === null &&
        ![
          process.env.NEXT_PUBLIC_STRIPE_POWERON_THREE_YEAR_PRICE_ID,
          process.env.NEXT_PUBLIC_STRIPE_PIPELINES_THREE_YEAR_PRICE_ID,
        ].includes(subscription.priceId) && (
          <div className="mt-8">
            <Card>
              <CardHeader className="p-4 sm:p-8">
                <CardTitle className="w-full text-base sm:text-lg">
                  Save {calculatePercentage()} from your subscription
                </CardTitle>
                <CardDescription className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                  <div className="flex flex-wrap items-center gap-1 text-sm">
                    <span>Change billing to</span>
                    <span>
                      {getPaymentMethodByPriceId(subscription.priceId) ===
                      PaymentMethods.MONTHLY.type
                        ? "yearly"
                        : "every 3-years"}
                    </span>
                    <span>to save</span>
                    <span className="font-bold text-emerald-400">
                      {calculateSavings()}
                    </span>
                    {getPaymentMethodByPriceId(subscription.priceId) ===
                      PaymentMethods.MONTHLY.type && <span>a year</span>}
                    <span>on</span>
                    {product === "poweron-pipelines" ? (
                      <span>PowerOn Pipelines</span>
                    ) : (
                      <span>
                        PowerOn Pro
                        <sup className="font-bold text-brand-accent">+</sup>
                      </span>
                    )}
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => handleManageSubscription(true)}
                    className="w-full text-sm sm:w-auto"
                  >
                    <Sparkles className="size-4" />
                    <span className="sm:hidden">
                      {getPaymentMethodByPriceId(subscription.priceId) ===
                      PaymentMethods.MONTHLY.type
                        ? "Yearly"
                        : "3-Year"}{" "}
                      plan
                    </span>
                    <span className="hidden sm:inline">
                      Change to{" "}
                      {getPaymentMethodByPriceId(subscription.priceId) ===
                      PaymentMethods.MONTHLY.type
                        ? "yearly"
                        : "3-Year"}{" "}
                      plan
                    </span>
                  </Button>
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        )}
      <div className="mt-8">
        {product === "poweron-pro" && (
          <SubscriptionPowerOnPro
            isAdmin={isAdmin}
            isMatchLoading={isMatchLoading}
            user={user}
            organization={organization}
            subscription={subscription}
            apiKeys={apiKeys}
            paymentMethod={paymentMethod}
            taxExemption={taxExemption}
            members={members}
            onMatch={debounceSubscriptionMatch}
            onQuantityChange={handleSubscriptionQuantity}
            onSubscribe={handleSubscribe}
          />
        )}
        {product === "poweron-pipelines" && (
          <SubscriptionPowerOnPipelines
            isAdmin={isAdmin}
            isMatchLoading={isMatchLoading}
            user={user}
            organization={organization}
            subscription={subscription}
            apiKeys={apiKeys}
            paymentMethod={paymentMethod}
            taxExemption={taxExemption}
            members={members}
            onMatch={debounceSubscriptionMatch}
            onQuantityChange={handleSubscriptionQuantity}
            onSubscribe={handleSubscribe}
          />
        )}
      </div>
      <MsgQuestion name="Subscriptions" />
      {/* Subscription payment method dialog */}
      <SubscriptionPaymentMethodDialog
        controlledOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSuccess={(collectionMethod, customer, quantity, promotionCodeId) => {
          handleSubscribe(
            collectionMethod,
            customer,
            quantity,
            promotionCodeId,
          );
        }}
        customerId={organization?.customerId || "UNASSIGNED"}
        product={product}
        subscriptionMetadata={getSubscriptionMetadata(paymentMethod)}
      />
    </>
  );
}
